import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  logMediaArchived,
  logMediaTagged,
  logMediaUntagged,
  logMediaVisibilityChanged,
} from "@/utils/action-logger";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Download,
  Image as ImageIcon,
  Video as VideoIcon,
  Music2,
  FileText,
  Globe2,
  Lock,
  LayoutGrid,
  X,
} from "lucide-react";

type MediaCategory = "all" | "photos" | "videos" | "audio" | "documents";

type ObjectMediaRow = Tables<"object_media">;
type ObjectMediaAlbumRow = Tables<"object_media_album">;
type ObjectMediaTaggedRow = {
  id: string;
  om_id: string;
  oma_id: string;
  tagged_by: string;
  create_time: string;
  archived: boolean;
  archived_by: string | null;
  archive_date: string | null;
};

function getCategoryForFile(file: File): MediaCategory | null {
  if (file.type.startsWith("image/")) return "photos";
  if (file.type.startsWith("video/")) return "videos";
  if (file.type.startsWith("audio/")) return "audio";

  // Fallback to extension for documents
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const documentExts = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"];
  if (documentExts.includes(ext)) return "documents";

  return null;
}

function getCategoryForRow(row: ObjectMediaRow): MediaCategory {
  if (row.is_image) return "photos";
  if (row.is_video) return "videos";
  if (row.is_audio) return "audio";
  return "documents";
}

const BIG_FILE_THRESHOLD_BYTES = 30 * 1024 * 1024; // 30 MB
const MEDIA_API_BASE_URL = import.meta.env
  .VITE_MEDIA_API_BASE_URL as string | undefined;

export default function AdminMediaManager() {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState<MediaCategory>("all");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaItems, setMediaItems] = useState<ObjectMediaRow[]>([]);
  const [albums, setAlbums] = useState<ObjectMediaAlbumRow[]>([]);
  const [isLoadingAlbums, setIsLoadingAlbums] = useState(false);
  const [activeParentAlbumId, setActiveParentAlbumId] = useState<string | "all">("all");
  const [activeSubAlbumId, setActiveSubAlbumId] = useState<string | "all">("all");
  const [isCreateAlbumOpen, setIsCreateAlbumOpen] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [subAlbumName, setSubAlbumName] = useState("");
  const [albumMode, setAlbumMode] = useState<"new" | "sub">("new");
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [gradingYear, setGradingYear] = useState<string>("");
  const [gradingQuarter, setGradingQuarter] = useState<string>("");
  const [gradingGroup, setGradingGroup] = useState<string>("");
  const [gradingMonth, setGradingMonth] = useState<string>("");
  const [gradingRegion, setGradingRegion] = useState<string>("");
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [selectedTagAlbumIds, setSelectedTagAlbumIds] = useState<Set<string>>(new Set());
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [mediaMetadataSavingId, setMediaMetadataSavingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeGradingYearFilter, setActiveGradingYearFilter] = useState<string | "all">("all");
  const [activeGradingQuarterFilter, setActiveGradingQuarterFilter] = useState<string | "all">("all");
  const [uploadTotalFiles, setUploadTotalFiles] = useState(0);
  const [uploadCompletedFiles, setUploadCompletedFiles] = useState(0);
  const [currentUploadFileName, setCurrentUploadFileName] = useState<string | null>(null);
  const [mediaTags, setMediaTags] = useState<
    Record<
      string,
      {
        id: string;
        album: ObjectMediaAlbumRow;
      }[]
    >
  >({});

  useEffect(() => {
    fetchMedia();
    fetchAlbums();

    // Real-time updates so new uploads from other devices appear without full reload
    const channel = supabase
      .channel("object_media_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "object_media" },
        () => {
          // Refresh without toggling loading UI — otherwise the list unmounts and scroll jumps to top.
          void fetchMedia({ background: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchMediaTags = async (albumsList?: ObjectMediaAlbumRow[]) => {
    try {
      const albumsToUse = albumsList ?? albums;
      if (!albumsToUse || albumsToUse.length === 0) {
        setMediaTags({});
        return;
      }

      const { data, error } = await (supabase as any)
        .from("object_media_tagged")
        .select("*")
        .eq("archived", false);

      if (error) {
        console.error("Error loading media tags:", error);
        toast({
          variant: "destructive",
          title: "Error loading tags",
          description: "There was a problem fetching media tags.",
        });
        return;
      }

      const albumMap = new Map<string, ObjectMediaAlbumRow>();
      albumsToUse.forEach((album) => {
        albumMap.set(album.id, album);
      });

      const tagsByMedia: Record<
        string,
        {
          id: string;
          album: ObjectMediaAlbumRow;
        }[]
      > = {};

      (data as unknown as ObjectMediaTaggedRow[]).forEach((tag) => {
        const album = albumMap.get(tag.oma_id);
        if (!album) return;
        if (!tagsByMedia[tag.om_id]) {
          tagsByMedia[tag.om_id] = [];
        }
        tagsByMedia[tag.om_id].push({
          id: tag.id,
          album,
        });
      });

      setMediaTags(tagsByMedia);
    } catch (error) {
      console.error("Unexpected error loading media tags:", error);
    }
  };

  const fetchMedia = async (options?: { background?: boolean }) => {
    const showLoading = !options?.background;
    try {
      if (showLoading) {
        setIsLoadingMedia(true);
      }
      const { data, error } = await supabase
        .from("object_media")
        .select("*")
        .eq("archived", false)
        .order("create_time", { ascending: false });

      if (error) throw error;
      setMediaItems((data as ObjectMediaRow[]) ?? []);
    } catch (error) {
      console.error("Error loading media:", error);
      toast({
        variant: "destructive",
        title: "Error loading media",
        description: "There was a problem fetching media items.",
      });
    } finally {
      if (showLoading) {
        setIsLoadingMedia(false);
      }
    }
  };

  const fetchAlbums = async () => {
    try {
      setIsLoadingAlbums(true);
      const { data, error } = await supabase
        .from("object_media_album")
        .select("*")
        .eq("archived", false)
        .order("album_name", { ascending: true });

      if (error) throw error;
      const albumsData = (data as ObjectMediaAlbumRow[]) ?? [];
      setAlbums(albumsData);
      await fetchMediaTags(albumsData);
    } catch (error) {
      console.error("Error loading albums:", error);
      toast({
        variant: "destructive",
        title: "Error loading albums",
        description: "There was a problem fetching albums.",
      });
    } finally {
      setIsLoadingAlbums(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleTagAlbumSelection = (albumId: string) => {
    setSelectedTagAlbumIds((prev) => {
      const next = new Set(prev);
      if (next.has(albumId)) {
        next.delete(albumId);
      } else {
        next.add(albumId);
      }
      return next;
    });
  };

  const handleApplyTags = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to tag media.",
      });
      return;
    }

    if (selectedIds.size === 0) {
      toast({
        variant: "destructive",
        title: "No media selected",
        description: "Please select one or more media items to tag.",
      });
      return;
    }

    if (selectedTagAlbumIds.size === 0) {
      toast({
        variant: "destructive",
        title: "No tags selected",
        description: "Please choose at least one album or sub-folder to tag.",
      });
      return;
    }

    setIsSavingTags(true);

    try {
      const mediaIds = Array.from(selectedIds);
      const albumIds = Array.from(selectedTagAlbumIds);

      // For logging: pre-build album lookup and track which albums were applied per media
      const albumById = new Map(albums.map((a) => [a.id, a]));
      const appliedAlbumsByMedia: Record<string, string[]> = {};

      for (const mediaId of mediaIds) {
        for (const albumId of albumIds) {
          const { data: existingData, error: existingError } = await (supabase as any)
            .from("object_media_tagged")
            .select("id, archived")
            .eq("om_id", mediaId)
            .eq("oma_id", albumId)
            .order("create_time", { ascending: false })
            .limit(1);

          if (existingError) {
            throw existingError;
          }

          const existing = (existingData as unknown as Pick<ObjectMediaTaggedRow, "id" | "archived">[] | null)?.[0];

          if (existing && existing.archived === false) {
            // Tag already exists and is active; skip
            continue;
          }

          const album = albumById.get(albumId);

          if (existing && existing.archived === true) {
            const { error: updateError } = await (supabase as any)
              .from("object_media_tagged")
              .update({
                archived: false,
                archived_by: null,
                archive_date: null,
                tagged_by: user.id,
                create_time: new Date().toISOString(),
              })
              .eq("id", existing.id);

            if (updateError) {
              throw updateError;
            }

            if (album) {
              if (!appliedAlbumsByMedia[mediaId]) appliedAlbumsByMedia[mediaId] = [];
              if (!appliedAlbumsByMedia[mediaId].includes(album.album_name)) {
                appliedAlbumsByMedia[mediaId].push(album.album_name);
              }
            }
          } else {
            const { error: insertError } = await (supabase as any)
              .from("object_media_tagged")
              .insert({
                om_id: mediaId,
                oma_id: albumId,
                tagged_by: user.id,
              });

            if (insertError) {
              throw insertError;
            }

            if (album) {
              if (!appliedAlbumsByMedia[mediaId]) appliedAlbumsByMedia[mediaId] = [];
              if (!appliedAlbumsByMedia[mediaId].includes(album.album_name)) {
                appliedAlbumsByMedia[mediaId].push(album.album_name);
              }
            }
          }
        }
      }

      // Log tagging only for media where we actually changed tags
      const mediaById = new Map(mediaItems.map((m) => [m.id, m]));
      for (const [mediaId, albumNames] of Object.entries(appliedAlbumsByMedia)) {
        const media = mediaById.get(mediaId);
        await logMediaTagged(
          user.id,
          mediaId,
          media?.name ?? media?.url.split("/").pop() ?? null,
          albumNames,
        );
      }

      toast({
        title: "Tags updated",
        description: "Tags have been applied to the selected media.",
      });

      setIsTagDialogOpen(false);
      setSelectedTagAlbumIds(new Set());
      setSelectedIds(new Set());
      await fetchMediaTags();
    } catch (error) {
      console.error("Error applying tags:", error);
      toast({
        variant: "destructive",
        title: "Error applying tags",
        description: "There was a problem applying tags to media items.",
      });
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleArchiveTag = async (tagId: string) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to remove tags.",
      });
      return;
    }

    try {
      // For logging: find which media+album this tag belongs to
      let mediaIdForTag: string | null = null;
      let albumNameForTag: string | undefined;
      Object.entries(mediaTags).some(([mediaId, tags]) => {
        const tag = tags.find((t) => t.id === tagId);
        if (tag) {
          mediaIdForTag = mediaId;
          albumNameForTag = tag.album.album_name;
          return true;
        }
        return false;
      });

      const { error } = await (supabase as any)
        .from("object_media_tagged")
        .update({
          archived: true,
          archived_by: user.id,
          archive_date: new Date().toISOString(),
        })
        .eq("id", tagId);

      if (error) {
        throw error;
      }

      await fetchMediaTags();

      if (mediaIdForTag) {
        const media = mediaItems.find((m) => m.id === mediaIdForTag);
        await logMediaUntagged(
          user.id,
          mediaIdForTag,
          media?.name ?? media?.url.split("/").pop() ?? null,
          albumNameForTag,
        );
      }

      toast({
        title: "Tag removed",
        description: "The tag has been removed from this media.",
      });
    } catch (error) {
      console.error("Error archiving tag:", error);
      toast({
        variant: "destructive",
        title: "Error removing tag",
        description: "There was a problem removing the tag.",
      });
    }
  };

  const handleSetVisibility = async (makePublic: boolean) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to change visibility.",
      });
      return;
    }

    if (selectedIds.size === 0) {
      toast({
        variant: "destructive",
        title: "No media selected",
        description: "Please select one or more media items to update.",
      });
      return;
    }

    setIsUpdatingVisibility(true);

    try {
      const ids = Array.from(selectedIds);

      // Snapshot current media for logging before refresh
      const mediaById = new Map(mediaItems.map((m) => [m.id, m]));

      const { error } = await supabase
        .from("object_media")
        .update({
          is_public: makePublic,
        })
        .in("id", ids);

      if (error) {
        throw error;
      }

      await fetchMedia();
      setSelectedIds(new Set());

      // Log each media visibility change
      for (const id of ids) {
        const media = mediaById.get(id);
        await logMediaVisibilityChanged(
          user.id,
          id,
          media?.name ?? media?.url.split("/").pop() ?? null,
          makePublic,
        );
      }

      toast({
        title: makePublic ? "Made public" : "Made private",
        description: makePublic
          ? "Selected media items are now public."
          : "Selected media items are now private.",
      });
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast({
        variant: "destructive",
        title: "Error updating visibility",
        description: "There was a problem updating media visibility.",
      });
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  const handlePatchMediaMetadata = async (
    item: ObjectMediaRow,
    updates: { description?: string | null; is_public?: boolean },
  ) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to update media details.",
      });
      return;
    }

    const patch: { description?: string | null; is_public?: boolean } = {};
    if (updates.description !== undefined) {
      patch.description = updates.description;
    }
    if (updates.is_public !== undefined) {
      if (!item.is_doc) {
        return;
      }
      patch.is_public = updates.is_public;
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    setMediaMetadataSavingId(item.id);
    try {
      const { error } = await supabase
        .from("object_media")
        .update(patch)
        .eq("id", item.id);

      if (error) {
        throw error;
      }

      setMediaItems((prev) =>
        prev.map((m) => (m.id === item.id ? { ...m, ...patch } : m)),
      );

      if (
        typeof patch.is_public === "boolean" &&
        patch.is_public !== item.is_public
      ) {
        await logMediaVisibilityChanged(
          user.id,
          item.id,
          item.name ?? item.url.split("/").pop() ?? null,
          patch.is_public,
        );
      }
    } catch (err) {
      console.error("Error updating media metadata:", err);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Could not save media details.",
      });
    } finally {
      setMediaMetadataSavingId(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to delete media.",
      });
      return;
    }

    if (selectedIds.size === 0) {
      toast({
        variant: "destructive",
        title: "No media selected",
        description: "Please select one or more media items to delete.",
      });
      return;
    }

    setIsDeleting(true);

    try {
      const ids = Array.from(selectedIds);

      // Snapshot current media for logging before refresh
      const mediaById = new Map(mediaItems.map((m) => [m.id, m]));

      const { error } = await supabase
        .from("object_media")
        .update({
          archived: true,
          archived_by: user.id,
          archive_date: new Date().toISOString(),
        })
        .in("id", ids);

      if (error) {
        throw error;
      }

      await fetchMedia();
      setSelectedIds(new Set());

      // Log each archived media item
      for (const id of ids) {
        const media = mediaById.get(id);
        await logMediaArchived(
          user.id,
          id,
          media?.name ?? media?.url.split("/").pop() ?? null,
        );
      }

      toast({
        title: "Media deleted",
        description: "Selected media items have been removed from the library.",
      });
    } catch (error) {
      console.error("Error deleting media:", error);
      toast({
        variant: "destructive",
        title: "Error deleting media",
        description: "There was a problem deleting media items.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateAlbum = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to create albums.",
      });
      return;
    }

    const isSub = albumMode === "sub";
    const parentId = isSub ? selectedParentId : null;

    if (isSub && !parentId) {
      toast({
        variant: "destructive",
        title: "Parent album required",
        description: "Please select a parent album for the sub-folder.",
      });
      return;
    }

    const parentAlbum = parentId
      ? albums.find((a) => a.id === parentId)
      : null;

    let finalName = "";

    if (!isSub) {
      const trimmedName = newAlbumName.trim();
      if (!trimmedName) {
        toast({
          variant: "destructive",
          title: "Album name required",
          description: "Please enter a name for the new album.",
        });
        return;
      }
      finalName = trimmedName;
    } else if (parentAlbum) {
      const parentName = parentAlbum.album_name.toLowerCase();

      if (parentName === "grading" || parentName === "taekwondo grading") {
        // Special grading wizard: Year + Quarter + Group
        if (!gradingYear || !gradingQuarter || !gradingGroup) {
          toast({
            variant: "destructive",
            title: "Grading details required",
            description: "Please select a year, quarter, and group for this grading album.",
          });
          return;
        }
        // Format: YEAR-Grading-QX-GY (e.g. 2025-Grading-Q1-G1)
        finalName = `${gradingYear}-Grading-${gradingQuarter}-${gradingGroup}`;
      } else if (parentName === "black belt grading") {
        // Black Belt grading: Year + Region (UK / USA)
        if (!gradingYear || !gradingRegion) {
          toast({
            variant: "destructive",
            title: "Grading details required",
            description: "Please select a year and region for this Black Belt grading album.",
          });
          return;
        }
        // Format: YEAR-BlackBelt-REGION (e.g. 2025-BlackBelt-UK)
        finalName = `${gradingYear}-BlackBelt-${gradingRegion}`;
      } else if (parentName === "lil dragon grading") {
        // Lil Dragon grading: Year + even month
        if (!gradingYear || !gradingMonth) {
          toast({
            variant: "destructive",
            title: "Grading details required",
            description: "Please select a year and month for this Lil Dragon grading album.",
          });
          return;
        }
        // Format: YEAR-LilDragon-MMM (e.g. 2025-LilDragon-Feb)
        finalName = `${gradingYear}-LilDragon-${gradingMonth}`;
      } else {
        const trimmedSub = subAlbumName.trim();
        if (!trimmedSub) {
          toast({
            variant: "destructive",
            title: "Sub-folder name required",
            description: "Please enter a name for the sub-folder.",
          });
          return;
        }
        finalName = trimmedSub;
      }
    } else {
      const trimmedSub = subAlbumName.trim();
      if (!trimmedSub) {
        toast({
          variant: "destructive",
          title: "Sub-folder name required",
          description: "Please enter a name for the sub-folder.",
        });
        return;
      }
      finalName = trimmedSub;
    }

    const lowerName = finalName.toLowerCase();

    // Uniqueness check: album_name + parent_id combination must be unique
    const exists = albums.some(
      (a) =>
        (a.parent_id ?? null) === parentId &&
        a.album_name.toLowerCase() === lowerName
    );
    if (exists) {
      toast({
        variant: "destructive",
        title: "Album name must be unique",
        description: "This parent already has an album with that name.",
      });
      return;
    }

    const albumString = finalName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");

    setIsCreatingAlbum(true);

    try {
      const { data, error } = await supabase
        .from("object_media_album")
        .insert({
          album_name: finalName,
          album_string: albumString,
          created_by: user.id,
          parent_id: parentId,
        })
        .select("*")
        .single();

      if (error) {
        // Handle unique constraint from DB
        if ((error as any).code === "23505") {
          toast({
            variant: "destructive",
            title: "Album name must be unique",
            description: "An album with that name already exists.",
          });
        } else {
          console.error("Error creating album:", error);
          toast({
            variant: "destructive",
            title: "Error creating album",
            description: "There was a problem creating the album.",
          });
        }
        return;
      }

      if (data) {
        setAlbums((prev) => [...prev, data as ObjectMediaAlbumRow]);
        toast({
          title: "Album created",
          description: `Album "${finalName}" has been created.`,
        });
        setIsCreateAlbumOpen(false);
        setNewAlbumName("");
        setSubAlbumName("");
        setAlbumMode("new");
        setSelectedParentId(null);
        setGradingYear("");
        setGradingQuarter("");
        const created = data as ObjectMediaAlbumRow;
        if (!created.parent_id) {
          setActiveParentAlbumId(created.id);
          setActiveSubAlbumId("all");
        } else {
          setActiveParentAlbumId(created.parent_id);
          setActiveSubAlbumId(created.id);
        }
      }
    } finally {
      setIsCreatingAlbum(false);
    }
  };

  const handleUpload = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to upload media.",
      });
      return;
    }

    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        variant: "destructive",
        title: "No files selected",
        description: "Please choose at least one file to upload.",
      });
      return;
    }

    setIsUploading(true);
    setUploadTotalFiles(selectedFiles.length);
    setUploadCompletedFiles(0);
    setCurrentUploadFileName(null);

    let successCount = 0;

    try {
      for (const file of Array.from(selectedFiles)) {
        setCurrentUploadFileName(file.name);

        const category = getCategoryForFile(file);

        if (!category) {
          toast({
            variant: "destructive",
            title: "Unsupported file type",
            description: `File "${file.name}" is not a supported image, video, audio, or document type.`,
          });
          continue;
        }

        const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
        const safeName = file.name.replace(/\s+/g, "_");

        const isBigFile = file.size > BIG_FILE_THRESHOLD_BYTES;
        let uploadUrl: string | null = null;
        const isImageCategory = category === "photos";

        let derivativeInfo: {
          originalUrl?: string;
          thumbUrl?: string;
          mediumUrl?: string;
          originalKey?: string;
          thumbKey?: string;
          mediumKey?: string;
        } = {};

        if (isImageCategory && MEDIA_API_BASE_URL) {
          // Preferred path for images: send file to the Node media server,
          // which stores original + WebP derivatives and returns their URLs.
          try {
            const apiBase = MEDIA_API_BASE_URL.replace(/\/+$/, "");
            const formData = new FormData();
            formData.append("file", file);
            formData.append("category", category);

            const resp = await fetch(`${apiBase}/media/upload`, {
              method: "POST",
              body: formData,
            });

            if (!resp.ok) {
              const errJson = (await resp.json().catch(() => null)) as
                | { error?: string }
                | null;
              console.error(
                "Media server upload failed",
                resp.status,
                errJson,
              );
              toast({
                variant: "destructive",
                title: "Upload failed",
                description:
                  `Could not upload "${file.name}" to media server.` +
                  (errJson?.error ? ` (${errJson.error})` : ""),
              });
              continue;
            }

            const json = (await resp.json()) as {
              originalUrl: string;
              thumbUrl: string;
              mediumUrl: string;
              originalKey: string;
              thumbKey: string;
              mediumKey: string;
            };

            uploadUrl = json.originalUrl;
            derivativeInfo = json;
          } catch (err) {
            console.error("Network error calling media server", err);
            toast({
              variant: "destructive",
              title: "Upload failed",
              description: `Network error while uploading "${file.name}".`,
            });
            continue;
          }
        } else {
          // Fallback path (and for non-images): use existing Supabase Edge Function
          // behaviour with base64 for small files or presigned URL for large files.
          if (!isBigFile) {
            // Existing small-file path: base64 JSON via supabase.functions.invoke
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            let binary = "";
            for (let i = 0; i < uint8Array.byteLength; i++) {
              binary += String.fromCharCode(uint8Array[i]);
            }
            const base64 = btoa(binary);

            const {
              data: uploadData,
              error: uploadError,
            } = await supabase.functions.invoke("upload-media", {
              body: {
                fileName: safeName,
                contentType: file.type || "application/octet-stream",
                category,
                base64,
              },
            });

            if (uploadError || !uploadData || !("url" in uploadData)) {
              console.error(
                "Failed to upload via Edge Function",
                uploadError,
                uploadData,
              );
              toast({
                variant: "destructive",
                title: "Upload failed",
                description: `Could not upload "${file.name}" to storage.`,
              });
              continue;
            }

            uploadUrl = (uploadData as { url: string }).url;
          } else {
            // New big-file path: get presigned PUT URL, then upload directly to Spaces
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();

              const headers: Record<string, string> = {
                apikey: SUPABASE_PUBLISHABLE_KEY,
              };
              if (session?.access_token) {
                headers["Authorization"] = `Bearer ${session.access_token}`;
              }

              const presignResponse = await fetch(
                `${SUPABASE_URL}/functions/v1/upload-media`,
                {
                  method: "POST",
                  headers: {
                    ...headers,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    fileName: safeName,
                    contentType: file.type || "application/octet-stream",
                    category,
                  }),
                },
              );

              const presignJson = (await presignResponse.json()) as
                | { uploadUrl?: string; publicUrl?: string; error?: string }
                | null;

              if (
                !presignResponse.ok ||
                !presignJson?.uploadUrl ||
                !presignJson.publicUrl
              ) {
                console.error(
                  "Failed to get presigned upload URL",
                  presignResponse.status,
                  presignJson,
                );
                toast({
                  variant: "destructive",
                  title: "Upload failed",
                  description:
                    `Could not upload "${file.name}" to storage.` +
                    (presignJson?.error ? ` (${presignJson.error})` : ""),
                });
                continue;
              }

              const directResponse = await fetch(presignJson.uploadUrl, {
                method: "PUT",
                headers: {
                  "Content-Type":
                    file.type || "application/octet-stream",
                  "x-amz-acl": "public-read",
                },
                body: file,
              });

              if (!directResponse.ok) {
                console.error(
                  "Direct upload to Spaces failed",
                  directResponse.status,
                  await directResponse.text(),
                );
                toast({
                  variant: "destructive",
                  title: "Upload failed",
                  description: `Storage rejected "${file.name}" during upload.`,
                });
                continue;
              }

              // Use the public URL from the function for our DB record
              uploadUrl = presignJson.publicUrl;
            } catch (err) {
              console.error(
                "Network error calling upload-media function",
                err,
              );
              toast({
                variant: "destructive",
                title: "Upload failed",
                description: `Network error while uploading "${file.name}".`,
              });
              continue;
            }
          }
        }

        if (!uploadUrl) {
          continue;
        }

        const isImage = category === "photos";
        const isVideo = category === "videos";
        const isAudio = category === "audio";
        const isDoc = category === "documents";

        const insertPayload: any = {
          url: uploadUrl,
          is_image: isImage,
          is_video: isVideo,
          is_audio: isAudio,
          is_doc: isDoc,
          extension,
          uploaded_by: user.id,
          is_public: isPublic,
          name: safeName,
        };

        if (isImage) {
          insertPayload.original_url = derivativeInfo.originalUrl || uploadUrl;
          insertPayload.thumb_url = derivativeInfo.thumbUrl || null;
          insertPayload.medium_url = derivativeInfo.mediumUrl || null;
          insertPayload.original_key = derivativeInfo.originalKey || null;
          insertPayload.thumb_key = derivativeInfo.thumbKey || null;
          insertPayload.medium_key = derivativeInfo.mediumKey || null;
        }

        const { error: insertError } = await supabase
          .from("object_media")
          .insert(insertPayload);

        if (insertError) {
          console.error("Error inserting media record:", insertError);
          toast({
            variant: "destructive",
            title: "Database error",
            description: `File "${file.name}" was uploaded but could not be saved in the database.`,
          });
          continue;
        }

        successCount += 1;
        setUploadCompletedFiles((prev) => prev + 1);
      }

      if (successCount > 0) {
        toast({
          title: "Upload complete",
          description: `Successfully uploaded ${successCount} file${successCount === 1 ? "" : "s"}.`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "No files uploaded",
          description: "All selected files failed to upload.",
        });
      }

      setIsUploadDialogOpen(false);
      setSelectedFiles(null);
      setSelectedIds(new Set());
      await fetchMedia();
    } catch (error) {
      console.error("Unexpected error during upload:", error);
      toast({
        variant: "destructive",
        title: "Unexpected error",
        description: "There was a problem uploading your files.",
      });
    } finally {
      setIsUploading(false);
      setCurrentUploadFileName(null);
      setUploadTotalFiles(0);
      setUploadCompletedFiles(0);
    }
  };

  const groupedMediaByDate = useMemo(() => {
    const groups: Record<string, ObjectMediaRow[]> = {};

    mediaItems.forEach((item) => {
      const createdAt = item.create_time;
      if (!createdAt) return;

      const dateKey = format(new Date(createdAt), "yyyy-MM-dd");
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });

    return groups;
  }, [mediaItems]);

  const mediaMatchesAlbumFilter = (item: ObjectMediaRow) => {
    // When ALL albums are selected, show everything regardless of tags
    if (activeParentAlbumId === "all") {
      return true;
    }

    const tagsForMedia = mediaTags[item.id] ?? [];
    if (tagsForMedia.length === 0) {
      return false;
    }

    const parentAlbum = albums.find((a) => a.id === activeParentAlbumId);
    const isGradingParent =
      parentAlbum &&
      (parentAlbum.album_name.toLowerCase() === "grading" ||
        parentAlbum.album_name.toLowerCase() === "taekwondo grading");

    if (isGradingParent) {
      // Special taxonomy for standard Grading (Taekwondo Grading)
      // 1) If a specific group (sub-folder) is selected, require that tag
      if (activeSubAlbumId !== "all") {
        return tagsForMedia.some((tag) => tag.album.id === activeSubAlbumId);
      }

      // 2) If a specific grading year AND quarter are selected, match any grading tag for that combo
      if (activeGradingYearFilter !== "all" && activeGradingQuarterFilter !== "all") {
        return tagsForMedia.some((tag) => {
          const name = tag.album.album_name;
          const parts = name.split("-");
          if (parts.length < 3) return false;
          const [year, , quarter] = parts;
          return year === activeGradingYearFilter && quarter === activeGradingQuarterFilter;
        });
      }

      // 3) If only a specific grading year is selected, match any grading tag for that year
      if (activeGradingYearFilter !== "all") {
        return tagsForMedia.some((tag) => {
          const name = tag.album.album_name;
          const [year] = name.split("-");
          return year === activeGradingYearFilter;
        });
      }

      // 4) Grading parent, ALL years & ALL quarters -> any grading tag
      return tagsForMedia.some((tag) => tag.album.parent_id === activeParentAlbumId);
    }

    // When a specific sub-folder is selected, require a tag for that folder
    if (activeSubAlbumId !== "all") {
      return tagsForMedia.some((tag) => tag.album.id === activeSubAlbumId);
    }

    // Parent album selected with ALL folders:
    // Only match media that are directly tagged with this parent album
    return tagsForMedia.some((tag) => tag.album.id === activeParentAlbumId);
  };

  const filteredMediaByCategory = useMemo(() => {
    const result: Record<string, ObjectMediaRow[]> = {};

    Object.entries(groupedMediaByDate).forEach(([date, items]) => {
      const filtered = items.filter((item) => {
        const matchesCategory =
          activeCategory === "all" ||
          getCategoryForRow(item) === activeCategory;
        if (!matchesCategory) return false;

        return mediaMatchesAlbumFilter(item);
      });
      if (filtered.length > 0) {
        result[date] = filtered;
      }
    });

    return result;
  }, [
    groupedMediaByDate,
    activeCategory,
    activeParentAlbumId,
    activeSubAlbumId,
    activeGradingYearFilter,
    mediaTags,
    albums,
  ]);

  const [visibleCount, setVisibleCount] = useState(40);

  useEffect(() => {
    setVisibleCount(40);
  }, [
    activeCategory,
    activeParentAlbumId,
    activeSubAlbumId,
    activeGradingYearFilter,
  ]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const {
    paginatedByDate,
    totalItemCount,
    hasMore,
  } = useMemo(() => {
    const entries = Object.entries(filteredMediaByCategory).sort(([a], [b]) =>
      a < b ? 1 : -1,
    );

    let remaining = visibleCount;
    const paginated: Record<string, ObjectMediaRow[]> = {};
    let total = 0;

    for (const [date, items] of entries) {
      total += items.length;
      if (remaining <= 0) continue;

      if (items.length <= remaining) {
        paginated[date] = items;
        remaining -= items.length;
      } else {
        paginated[date] = items.slice(0, remaining);
        remaining = 0;
      }
    }

    return {
      paginatedByDate: paginated,
      totalItemCount: total,
      hasMore: total > visibleCount,
    };
  }, [filteredMediaByCategory, visibleCount]);

  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoadingMedia) {
          setVisibleCount((prev) => prev + 40);
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMedia]);

  const getDerivedUrl = (
    item: ObjectMediaRow,
    variant: "thumb300" | "med1200",
  ) => {
    const anyItem = item as any;

    // Prefer explicit DB fields when present (new uploads)
    if (variant === "thumb300" && anyItem.thumb_url) {
      return anyItem.thumb_url as string;
    }
    if (variant === "med1200" && anyItem.medium_url) {
      return anyItem.medium_url as string;
    }

    // Fallback: derive from original URL (legacy uploads + backfill)
    const baseUrl = (anyItem.original_url as string | null) || item.url;

    try {
      const url = new URL(baseUrl);
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length === 0) return baseUrl;

      const filename = parts.pop()!;
      const dot = filename.lastIndexOf(".");
      const baseName = dot >= 0 ? filename.slice(0, dot) : filename;

      // New-style layout: /photos/<id>.<ext> -> /photos/thumb300|med1200/<id>.webp
      const photosIndex = parts.indexOf("photos");
      if (photosIndex !== -1) {
        const newParts = [
          "",
          ...parts.slice(0, photosIndex + 1),
          variant === "thumb300" ? "thumb300" : "med1200",
          `${baseName}.webp`,
        ];
        url.pathname = newParts.join("/").replace(/\/+/g, "/");
        return url.toString();
      }

      // Generic legacy layout: <dir>/<file> -> <dir>/thumb300|med1200/<file>.webp
      const lastDir = parts.pop() || "";
      const newPath = [
        "",
        ...parts,
        lastDir,
        variant === "thumb300" ? "thumb300" : "med1200",
        `${baseName}.webp`,
      ]
        .filter(Boolean)
        .join("/");

      url.pathname = newPath.replace(/\/+/g, "/");
      return url.toString();
    } catch {
      return baseUrl;
    }
  };

  const renderMediaPreview = (item: ObjectMediaRow) => {
    const category = getCategoryForRow(item);

    if (category === "photos") {
      const thumbUrl = getDerivedUrl(item, "thumb300");
      return (
        <div className="h-28 w-full rounded-md bg-slate-100 flex items-center justify-center overflow-hidden">
          <img
            src={thumbUrl}
            alt={item.name ?? "Photo"}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src !== item.url) {
                e.currentTarget.src = item.url;
              }
            }}
          />
        </div>
      );
    }

    if (category === "videos") {
      return (
        <div className="h-32 w-full rounded-md bg-black overflow-hidden">
          <video
            src={item.url}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        </div>
      );
    }

    if (category === "audio") {
      return (
        <div className="flex h-32 w-full items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
          <Music2 className="h-8 w-8 mr-2" />
          <span className="text-sm">Audio</span>
        </div>
      );
    }

    return (
      <div className="flex h-32 w-full items-center justify-center rounded-md bg-slate-50 text-slate-700">
        <FileText className="h-8 w-8 mr-2" />
        <span className="text-sm">Document</span>
      </div>
    );
  };

  const getMediaDownloadName = (item: ObjectMediaRow) => {
    const fallbackName = item.url.split("/").pop() ?? "download";
    const rawName = item.name?.trim() || fallbackName;
    const baseName = rawName.replace(/\.[a-z0-9]+$/i, "");
    const extension = item.extension?.trim() || fallbackName.split(".").pop() || "bin";
    return `${baseName}.${extension}`;
  };

  const handleDownloadMedia = async (item: ObjectMediaRow) => {
    try {
      const response = await fetch(item.url);

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = getMediaDownloadName(item);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading media:", error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: `Could not download "${item.name ?? item.url.split("/").pop()}".`,
      });
    }
  };

  const renderPreviewDialogContent = (item: ObjectMediaRow) => {
    const category = getCategoryForRow(item);

    if (category === "photos") {
      const mediumUrl = getDerivedUrl(item, "med1200");
      return (
        <div className="space-y-3">
          <img
            src={mediumUrl}
            alt={item.name ?? "Photo preview"}
            className="w-full max-h-[70vh] object-contain rounded-md bg-slate-100"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src !== item.url) {
                e.currentTarget.src = item.url;
              }
            }}
          />
        </div>
      );
    }

    if (category === "videos") {
      return (
        <div className="space-y-3">
          <video
            src={item.url}
            controls
            className="w-full max-h-[70vh] rounded-md bg-black"
          />
        </div>
      );
    }

    if (category === "audio") {
      return (
        <div className="space-y-3">
          <audio
            src={item.url}
            controls
            className="w-full"
          />
        </div>
      );
    }

    // Documents and other types
    return (
      <div className="space-y-3">
        <div className="h-72 w-full rounded-md border bg-slate-50 overflow-hidden">
          <iframe
            src={item.url}
            className="h-full w-full"
            title={item.name ?? "Document preview"}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          If the document does not render correctly, you can open it in a new tab.
        </p>
        <Button asChild variant="outline" size="sm">
          <a href={item.url} target="_blank" rel="noreferrer">
            Open in new tab
          </a>
        </Button>
      </div>
    );
  };

  const toolbar = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        <h2 className="text-2xl font-semibold text-taekwondo-red mb-6">Media Library</h2>
        <p className="text-sm text-muted-foreground">
          Upload and manage photos, videos, audio, and documents for the site.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-2 w-full sm:w-auto">
        {/* Group 1: album & upload actions */}
        <div className="flex w-full sm:w-auto gap-2">
          <Dialog open={isCreateAlbumOpen} onOpenChange={setIsCreateAlbumOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                disabled={isUploading || selectedIds.size > 0}
                className="w-1/2 sm:w-auto"
              >
                Create Album
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Album</DialogTitle>
                <DialogDescription>
                  Choose a unique name for this album or create a sub-folder under an existing album.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Mode toggle */}
                <div className="flex items-center justify-between rounded-md border px-2 py-1.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    Album type
                  </span>
                  <div className="inline-flex rounded-md border bg-muted p-0.5">
                    <Button
                      type="button"
                      size="sm"
                      variant={albumMode === "new" ? "default" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => setAlbumMode("new")}
                      disabled={isCreatingAlbum}
                    >
                      New album
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={albumMode === "sub" ? "default" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        const hasParents = albums.some((a) => !a.parent_id);
                        if (!hasParents) return;
                        setAlbumMode("sub");
                      }}
                      disabled={
                        isCreatingAlbum || !albums.some((a) => !a.parent_id)
                      }
                    >
                      Sub folder
                    </Button>
                  </div>
                </div>

                {/* New album fields */}
                {albumMode === "new" && (
                  <div className="space-y-2">
                    <Label htmlFor="albumName">Album name</Label>
                    <Input
                      id="albumName"
                      value={newAlbumName}
                      onChange={(e) => setNewAlbumName(e.target.value)}
                      placeholder="e.g. Grading 2025"
                      disabled={isCreatingAlbum}
                    />
                  </div>
                )}

                {/* Sub folder fields */}
                {albumMode === "sub" && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Parent album</Label>
                      <Select
                        value={selectedParentId ?? ""}
                        onValueChange={(value) => setSelectedParentId(value)}
                        disabled={isCreatingAlbum}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select parent album" />
                        </SelectTrigger>
                        <SelectContent>
                          {albums
                            .filter((a) => !a.parent_id)
                            .map((album) => (
                              <SelectItem key={album.id} value={album.id}>
                                {album.album_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(() => {
                      const parentAlbum = selectedParentId
                        ? albums.find((a) => a.id === selectedParentId)
                        : null;

                      if (!parentAlbum) {
                        return null;
                      }

                      const parentName = parentAlbum.album_name.toLowerCase();
                      const isStandardGradingParent =
                        parentName === "grading" ||
                        parentName === "taekwondo grading";
                      const isBlackBeltGradingParent =
                        parentName === "black belt grading";
                      const isLilDragonGradingParent =
                        parentName === "lil dragon grading";

                      if (isStandardGradingParent) {
                        const currentYear = new Date().getFullYear();
                        const years = Array.from(
                          { length: currentYear - 1979 },
                          (_, i) => String(currentYear - i)
                        );
                        const quarters = ["Q1", "Q2", "Q3", "Q4"];
                        const groups = ["G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8"];

                        return (
                          <div className="space-y-2">
                            <Label>Grading period</Label>
                            <div className="flex gap-2">
                              <Select
                                value={gradingYear}
                                onValueChange={setGradingYear}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Year" />
                                </SelectTrigger>
                                <SelectContent>
                                  {years.map((year) => (
                                    <SelectItem key={year} value={year}>
                                      {year}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={gradingQuarter}
                                onValueChange={setGradingQuarter}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Quarter" />
                                </SelectTrigger>
                                <SelectContent>
                                  {quarters.map((q) => (
                                    <SelectItem key={q} value={q}>
                                      {q}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label>Group</Label>
                              <Select
                                value={gradingGroup}
                                onValueChange={setGradingGroup}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select group (G1–G8)" />
                                </SelectTrigger>
                                <SelectContent>
                                  {groups.map((g) => (
                                    <SelectItem key={g} value={g}>
                                      {g}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      }

                      if (isLilDragonGradingParent) {
                        const currentYear = new Date().getFullYear();
                        const years = Array.from(
                          { length: currentYear - 1979 },
                          (_, i) => String(currentYear - i)
                        );
                        const evenMonths = [
                          { value: "Feb", label: "February" },
                          { value: "Apr", label: "April" },
                          { value: "Jun", label: "June" },
                          { value: "Aug", label: "August" },
                          { value: "Oct", label: "October" },
                          { value: "Dec", label: "December" },
                        ];

                        return (
                          <div className="space-y-2">
                            <Label>Grading period (Lil Dragons)</Label>
                            <div className="flex gap-2">
                              <Select
                                value={gradingYear}
                                onValueChange={setGradingYear}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Year" />
                                </SelectTrigger>
                                <SelectContent>
                                  {years.map((year) => (
                                    <SelectItem key={year} value={year}>
                                      {year}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={gradingMonth}
                                onValueChange={setGradingMonth}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Month" />
                                </SelectTrigger>
                                <SelectContent>
                                  {evenMonths.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>
                                      {m.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      }

                      if (isBlackBeltGradingParent) {
                        const currentYear = new Date().getFullYear();
                        const years = Array.from(
                          { length: currentYear - 1979 },
                          (_, i) => String(currentYear - i)
                        );
                        const regions = [
                          { value: "UK", label: "UK" },
                          { value: "USA", label: "USA" },
                        ];

                        return (
                          <div className="space-y-2">
                            <Label>Grading period (Black Belt)</Label>
                            <div className="flex gap-2">
                              <Select
                                value={gradingYear}
                                onValueChange={setGradingYear}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Year" />
                                </SelectTrigger>
                                <SelectContent>
                                  {years.map((year) => (
                                    <SelectItem key={year} value={year}>
                                      {year}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={gradingRegion}
                                onValueChange={setGradingRegion}
                                disabled={isCreatingAlbum}
                              >
                                <SelectTrigger className="w-1/2">
                                  <SelectValue placeholder="Region" />
                                </SelectTrigger>
                                <SelectContent>
                                  {regions.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>
                                      {r.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          <Label htmlFor="subAlbumName">Sub-folder name</Label>
                          <Input
                            id="subAlbumName"
                            value={subAlbumName}
                            onChange={(e) => setSubAlbumName(e.target.value)}
                            placeholder="e.g. Photo Set A"
                            disabled={isCreatingAlbum}
                          />
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateAlbumOpen(false)}
                    disabled={isCreatingAlbum}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateAlbum}
                    disabled={isCreatingAlbum}
                    className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
                  >
                    {isCreatingAlbum ? "Creating..." : "Create Album"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button
                disabled={isUploading || selectedIds.size > 0}
                className="w-1/2 sm:w-auto bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
              >
                Add Media
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Media</DialogTitle>
                <DialogDescription>
                  Select one or more files from your device to upload to the media library.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="files">Files</Label>
                  <Input
                    id="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  />
                  <p className="text-xs text-muted-foreground">
                    You can select multiple files. Supported types: images, videos, audio, and common documents.
                  </p>
                </div>

                {uploadTotalFiles > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">
                        Uploading {uploadCompletedFiles} of {uploadTotalFiles} file
                        {uploadTotalFiles === 1 ? "" : "s"}
                      </span>
                      {currentUploadFileName && (
                        <span className="text-muted-foreground line-clamp-1 max-w-[220px]">
                          {currentUploadFileName}
                        </span>
                      )}
                    </div>
                    <Progress
                      value={
                        uploadTotalFiles === 0
                          ? 0
                          : (uploadCompletedFiles / uploadTotalFiles) * 100
                      }
                    />
                  </div>
                )}

                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <Label htmlFor="isPublic" className="flex items-center gap-1 text-sm font-medium">
                      <Globe2 className="h-4 w-4" />
                      Public
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, uploaded media will be marked as public.
                    </p>
                  </div>
                  <Switch
                    id="isPublic"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsUploadDialogOpen(false)}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
                  >
                    {isUploading ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Visual spacer between primary and selection actions */}
        <div className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />

        {/* Group 2: selection-based actions */}
        <div className="flex w-full sm:w-auto flex-wrap gap-2">
          <Dialog open={isTagDialogOpen} onOpenChange={(open) => {
            setIsTagDialogOpen(open);
            if (!open) {
              setSelectedTagAlbumIds(new Set());
            }
          }}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                disabled={selectedIds.size === 0}
                className="flex-1 sm:flex-none"
              >
                Tag
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Tag Media</DialogTitle>
                <DialogDescription>
                  Choose one or more albums or sub-folders to tag the selected media items.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  {selectedIds.size} media item{selectedIds.size === 1 ? "" : "s"} selected.
                </p>
                <div className="border rounded-md p-2 h-60 overflow-y-auto">
                  {albums.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No albums available yet. Create an album first to use tags.
                    </p>
                  ) : (
                    albums
                      .filter((album) => !album.parent_id)
                      .map((parent) => (
                        <div key={parent.id} className="mb-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`tag-album-${parent.id}`}
                              checked={selectedTagAlbumIds.has(parent.id)}
                              onCheckedChange={() => toggleTagAlbumSelection(parent.id)}
                            />
                            <Label
                              htmlFor={`tag-album-${parent.id}`}
                              className="text-sm font-medium"
                            >
                              {parent.album_name}
                            </Label>
                          </div>
                          {albums
                            .filter((child) => child.parent_id === parent.id)
                            .map((child) => (
                              <div
                                key={child.id}
                                className="ml-6 mt-0.5 flex items-center gap-2"
                              >
                                <Checkbox
                                  id={`tag-album-${child.id}`}
                                  checked={selectedTagAlbumIds.has(child.id)}
                                  onCheckedChange={() => toggleTagAlbumSelection(child.id)}
                                />
                                <Label
                                  htmlFor={`tag-album-${child.id}`}
                                  className="text-xs"
                                >
                                  {child.album_name}
                                </Label>
                              </div>
                            ))}
                        </div>
                      ))
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsTagDialogOpen(false)}
                    disabled={isSavingTags}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApplyTags}
                    disabled={isSavingTags || selectedTagAlbumIds.size === 0}
                    className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
                  >
                    {isSavingTags ? "Saving..." : "Apply Tags"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            disabled={selectedIds.size === 0 || isUpdatingVisibility}
            className="flex-1 sm:flex-none"
            onClick={() => handleSetVisibility(true)}
          >
            Public
          </Button>
          <Button
            variant="outline"
            disabled={selectedIds.size === 0 || isUpdatingVisibility}
            className="flex-1 sm:flex-none"
            onClick={() => handleSetVisibility(false)}
          >
            Private
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="default"
                disabled={selectedIds.size === 0 || isDeleting}
                className="flex-1 sm:flex-none bg-taekwondo-red hover:bg-taekwondo-red/90 text-white"
              >
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete selected media?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will archive the selected media items and remove them from the media library. You can&apos;t easily undo this action.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteSelected}
                  className="bg-taekwondo-red hover:bg-taekwondo-red/90 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      {toolbar}

      <Tabs
        value={activeCategory}
        onValueChange={(value) => setActiveCategory(value as MediaCategory)}
        className="w-full"
      >
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="all" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">All</span>
          </TabsTrigger>
          <TabsTrigger value="photos" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Photos</span>
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
            <VideoIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Videos</span>
          </TabsTrigger>
          <TabsTrigger value="audio" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
            <Music2 className="h-4 w-4" />
            <span className="hidden sm:inline">Audio</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Documents</span>
          </TabsTrigger>
        </TabsList>

        {/* Album Filter Bar - parents only */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground mr-1">
            Albums:
          </span>
          <Button
            variant={activeParentAlbumId === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setActiveParentAlbumId("all");
              setActiveSubAlbumId("all");
              setActiveGradingYearFilter("all");
            }}
          >
            ALL
          </Button>
          {isLoadingAlbums && (
            <span className="text-xs text-muted-foreground">Loading albums...</span>
          )}
          {!isLoadingAlbums &&
            albums
              .filter((album) => !album.parent_id)
              .map((album) => (
                <Button
                  key={album.id}
                  variant={activeParentAlbumId === album.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setActiveParentAlbumId(album.id);
                    setActiveSubAlbumId("all");
                    setActiveGradingYearFilter("all");
                  }}
                >
                  {album.album_name}
                </Button>
              ))}
        </div>

        {/* Folder Filter Bar (for selected parent) */}
        {activeParentAlbumId !== "all" && (
          (() => {
            const parentAlbum = albums.find(
              (album) => album.id === activeParentAlbumId
            );
            const children = albums.filter(
              (album) => album.parent_id === activeParentAlbumId
            );
            if (!parentAlbum || children.length === 0) return null;

            const parentName = parentAlbum.album_name.toLowerCase();
            const isStandardGradingParent =
              parentName === "grading" || parentName === "taekwondo grading";
            const isLilDragonGradingParent =
              parentName === "lil dragon grading";
            const isBlackBeltGradingParent =
              parentName === "black belt grading";
            const isGradingParent =
              isStandardGradingParent ||
              isLilDragonGradingParent ||
              isBlackBeltGradingParent;

            if (!isGradingParent) {
              return (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Folders:
                  </span>
                  <Button
                    variant={activeSubAlbumId === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveSubAlbumId("all")}
                  >
                    ALL
                  </Button>
                  {children.map((album) => (
                    <Button
                      key={album.id}
                      variant={activeSubAlbumId === album.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveSubAlbumId(album.id)}
                    >
                      {album.album_name}
                    </Button>
                  ))}
                </div>
              );
            }

            // Special taxonomy for Grading:
            // - Grading / Taekwondo Grading: YEAR -> QUARTER -> GROUP
            // - Lil Dragon Grading: YEAR -> MONTH (even months only)
            // - Black Belt Grading: YEAR -> REGION
            const gradingChildren = children;

            if (isStandardGradingParent) {
              type GroupEntry = { id: string; quarter: string; group: string };

              const byYear = gradingChildren.reduce<
                Record<string, Record<string, GroupEntry[]>>
              >((acc, album) => {
                const parts = album.album_name.split("-");
                if (parts.length < 4) return acc;
                const [year, , quarter, group] = parts;
                if (!year || !quarter || !group) return acc;
                if (!acc[year]) acc[year] = {};
                if (!acc[year][quarter]) acc[year][quarter] = [];
                acc[year][quarter].push({ id: album.id, quarter, group });
                return acc;
              }, {});

              const years = Object.keys(byYear).sort((a, b) => (a < b ? 1 : -1));
              const quarterOrder = ["Q1", "Q2", "Q3", "Q4"];

              const quartersForYear =
                activeGradingYearFilter !== "all" && byYear[activeGradingYearFilter]
                  ? Object.keys(byYear[activeGradingYearFilter]).sort(
                      (a, b) =>
                        quarterOrder.indexOf(a) - quarterOrder.indexOf(b),
                    )
                  : [];

              const groupsForSelection =
                activeGradingYearFilter !== "all" &&
                activeGradingQuarterFilter !== "all" &&
                byYear[activeGradingYearFilter] &&
                byYear[activeGradingYearFilter][activeGradingQuarterFilter]
                  ? byYear[activeGradingYearFilter][activeGradingQuarterFilter]
                  : [];

              return (
                <>
                  {/* Years row */}
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground mr-1">
                      Years:
                    </span>
                    <Button
                      variant={
                        activeGradingYearFilter === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setActiveGradingYearFilter("all");
                        setActiveGradingQuarterFilter("all");
                        setActiveSubAlbumId("all");
                      }}
                    >
                      ALL
                    </Button>
                    {years.map((year) => (
                      <Button
                        key={year}
                        variant={
                          activeGradingYearFilter === year ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          setActiveGradingYearFilter(year);
                          setActiveGradingQuarterFilter("all");
                          setActiveSubAlbumId("all");
                        }}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>

                  {/* Quarters row for selected year */}
                  {activeGradingYearFilter !== "all" && quartersForYear.length > 0 && (
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-muted-foreground mr-1">
                        Quarters:
                      </span>
                      <Button
                        variant={
                          activeGradingQuarterFilter === "all" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          setActiveGradingQuarterFilter("all");
                          setActiveSubAlbumId("all");
                        }}
                      >
                        ALL
                      </Button>
                      {quartersForYear.map((q) => (
                        <Button
                          key={q}
                          variant={
                            activeGradingQuarterFilter === q ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => {
                            setActiveGradingQuarterFilter(q);
                            setActiveSubAlbumId("all");
                          }}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* Groups row for selected year + quarter */}
                  {activeGradingYearFilter !== "all" &&
                    activeGradingQuarterFilter !== "all" &&
                    groupsForSelection.length > 0 && (
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground mr-1">
                          Groups:
                        </span>
                        <Button
                          variant={
                            activeSubAlbumId === "all" ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setActiveSubAlbumId("all")}
                        >
                          ALL
                        </Button>
                        {groupsForSelection.map((entry) => (
                          <Button
                            key={entry.id}
                            variant={
                              activeSubAlbumId === entry.id ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setActiveSubAlbumId(entry.id)}
                          >
                            {entry.group}
                          </Button>
                        ))}
                      </div>
                    )}
                </>
              );
            }

            // Lil Dragon / Black Belt grading: YEAR -> label (month / region)
            const gradingByYear = gradingChildren.reduce<
              Record<string, { id: string; label: string }[]>
            >((acc, album) => {
              const parts = album.album_name.split("-");
              if (parts.length !== 3) return acc;
              const [year, , label] = parts;
              if (!acc[year]) acc[year] = [];
              acc[year].push({ id: album.id, label });
              return acc;
            }, {});

            const years = Object.keys(gradingByYear).sort((a, b) =>
              a < b ? 1 : -1
            );

            return (
              <>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Years:
                  </span>
                  <Button
                    variant={
                      activeGradingYearFilter === "all" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => {
                      setActiveGradingYearFilter("all");
                      setActiveSubAlbumId("all");
                    }}
                  >
                    ALL
                  </Button>
                  {years.map((year) => (
                    <Button
                      key={year}
                      variant={
                        activeGradingYearFilter === year ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setActiveGradingYearFilter(year);
                        setActiveSubAlbumId("all");
                      }}
                    >
                      {year}
                    </Button>
                  ))}
                </div>
                {activeGradingYearFilter !== "all" && (
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground mr-1">
                      {isLilDragonGradingParent ? "Months:" : "Regions / Batches:"}
                    </span>
                    <Button
                      variant={
                        activeSubAlbumId === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setActiveSubAlbumId("all")}
                    >
                      ALL
                    </Button>
                    {(() => {
                      const entries = gradingByYear[activeGradingYearFilter] ?? [];
                      let ordered = entries;

                      if (isLilDragonGradingParent) {
                        const monthOrder = ["Feb", "Apr", "Jun", "Aug", "Oct", "Dec"];
                        ordered = [...entries].sort((a, b) => {
                          const aIndex = monthOrder.indexOf(a.label);
                          const bIndex = monthOrder.indexOf(b.label);
                          return aIndex - bIndex;
                        });
                      }

                      return ordered.map((entry) => (
                        <Button
                          key={entry.id}
                          variant={
                            activeSubAlbumId === entry.id ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setActiveSubAlbumId(entry.id)}
                        >
                          {entry.label}
                        </Button>
                      ));
                    })()}
                  </div>
                )}
              </>
            );
          })()
        )}

        <TabsContent value={activeCategory}>
          {isLoadingMedia ? (
            <p className="text-sm text-muted-foreground">Loading media...</p>
          ) : Object.keys(paginatedByDate).length === 0 ? (
            <p className="text-sm text-muted-foreground">No media found in this category yet.</p>
          ) : (
            <ScrollArea className="h-[480px] pr-4">
              <div className="space-y-6">
                {Object.entries(paginatedByDate)
                  .sort(([a], [b]) => (a < b ? 1 : -1))
                  .map(([dateKey, items]) => (
                    <div key={dateKey} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">
                          {format(new Date(dateKey), "EEEE, dd MMM yyyy")}
                        </h3>
                        <Badge variant="outline">{items.length}</Badge>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {items.map((item) => {
                          const isSelected = selectedIds.has(item.id);
                          const isDocument = getCategoryForRow(item) === "documents";
                          return (
                            <Card
                              key={item.id}
                              className={`overflow-hidden cursor-pointer transition-all border-2 ${
                                isSelected
                                  ? "border-taekwondo-blue ring-2 ring-taekwondo-blue/40"
                                  : "border-transparent hover:border-slate-300"
                              }`}
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't toggle selection when using document fields, inputs, or controls
                                if (
                                  target.closest(
                                    "button, a, input, textarea, [role=checkbox], label",
                                  )
                                ) {
                                  return;
                                }
                                toggleSelection(item.id);
                              }}
                            >
                              {renderMediaPreview(item)}
                              <div className="space-y-1 p-3">
                                <div className="flex items-center justify-between gap-1">
                                  <p className="line-clamp-1 text-xs font-medium">
                                    {item.name ?? item.url.split("/").pop()}
                                  </p>
                                  {item.is_public ? (
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-emerald-600 text-[9px] font-bold text-white">
                                      P
                                    </span>
                                  ) : (
                                    <Lock className="h-3 w-3 text-slate-500 shrink-0" />
                                  )}
                                </div>
                                <div
                                  className="space-y-2 border-t border-slate-100 pt-2"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <div className="space-y-1">
                                    <Label
                                      htmlFor={`media-desc-${item.id}`}
                                      className="text-[10px] font-medium text-muted-foreground"
                                    >
                                      Description
                                    </Label>
                                    <Textarea
                                      id={`media-desc-${item.id}`}
                                      className="min-h-[2.75rem] resize-y py-1.5 text-xs"
                                      defaultValue={item.description ?? ""}
                                      placeholder="Add a short description…"
                                      disabled={mediaMetadataSavingId === item.id}
                                      rows={2}
                                      onClick={(e) => e.stopPropagation()}
                                      onBlur={(e) => {
                                        const v = e.currentTarget.value.trim();
                                        const current = (item.description ?? "").trim();
                                        if (v === current) {
                                          return;
                                        }
                                        void handlePatchMediaMetadata(item, {
                                          description: v || null,
                                        });
                                      }}
                                    />
                                  </div>
                                  {isDocument && (
                                    <div className="flex items-start gap-2">
                                      <Checkbox
                                        id={`doc-members-${item.id}`}
                                        className="mt-0.5"
                                        checked={item.is_public}
                                        disabled={mediaMetadataSavingId === item.id}
                                        onCheckedChange={(c) => {
                                          const next = c === true;
                                          if (next === item.is_public) {
                                            return;
                                          }
                                          void handlePatchMediaMetadata(item, {
                                            is_public: next,
                                          });
                                        }}
                                      />
                                      <Label
                                        htmlFor={`doc-members-${item.id}`}
                                        className="text-xs font-normal leading-snug cursor-pointer"
                                      >
                                        Available to members
                                      </Label>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-[10px] text-muted-foreground">
                                    {item.extension.toUpperCase()} •{" "}
                                    {format(new Date(item.create_time), "HH:mm")}
                                  </p>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      className="h-6 w-6 bg-taekwondo-blue p-0 text-white hover:bg-taekwondo-blue/90"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        void handleDownloadMedia(item)
                                      }}
                                      aria-label={`Download ${item.name ?? item.url.split("/").pop()}`}
                                      title="Download"
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 px-2 text-[10px]"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          Preview
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent className="max-w-2xl">
                                        <DialogHeader>
                                          <DialogTitle className="text-sm font-semibold">
                                            {item.name ?? item.url.split("/").pop()}
                                          </DialogTitle>
                                          <DialogDescription className="text-xs">
                                            {item.extension.toUpperCase()} •{" "}
                                            {format(new Date(item.create_time), "dd MMM yyyy HH:mm")}
                                          </DialogDescription>
                                        </DialogHeader>
                                        {renderPreviewDialogContent(item)}
                                      </DialogContent>
                                    </Dialog>
                                  </div>
                                </div>
                                {mediaTags[item.id] && mediaTags[item.id].length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {mediaTags[item.id].map((tag) => (
                                      <div
                                        key={tag.id}
                                        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700"
                                      >
                                        <span className="mr-1">
                                          {tag.album.album_name}
                                        </span>
                                        <button
                                          type="button"
                                          className="ml-1 rounded-full p-0.5 hover:bg-slate-200"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleArchiveTag(tag.id);
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                {/* Sentinel for infinite scroll */}
                {hasMore && <div ref={loadMoreRef} className="h-px w-full" />}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}


